import PropTypes from 'prop-types';
import {lazy, memo, Suspense, useCallback, useId, useRef} from 'react';

import BaseLink from '@amblique/base/app/components/link';
import {urlHelper} from '@amblique/base/app/helpers/url-helper';
import {CloseIcon} from '@amblique/king-living/app/components/icons';
import {Image} from '@amblique/king-living/app/contentful/models/image';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Stack,
  Text,
  useDisclosure,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const loadKingLivingContentfulComponent = () => import('@amblique/king-living/app/components/contentful/component');

const ContentfulComponent = lazy(() => loadKingLivingContentfulComponent().then((mod) => ({default: mod.Component})));

/** Below Chakra `md` (48em): restore page scroll after drawer closes. */
const MOBILE_MAX_WIDTH_MEDIA = '(max-width: 767.98px)';

function restoreScrollAfterBodyUnlock(scrollY) {
  if (typeof window === 'undefined') {
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        window.scrollTo(0, scrollY);
      } catch {
        // Ignore environments where scrollTo fails.
      }
    });
  });
}

const TEASER_TILE_PLUS_BG = `linear-gradient(0deg, var(--chakra-colors-brandColors-greyscale20, rgba(27, 26, 24, 0.2)) 0%, var(--chakra-colors-brandColors-greyscale20, rgba(27, 26, 24, 0.2)) 100%), var(--chakra-colors-whiteAlpha-250, rgba(255, 255, 255, 0.1))`;

const PLUS_ICON_STYLES = {
  position: 'absolute',
  bottom: {base: '14px', md: '20px'},
  left: {base: '14px', md: '20px'},
  width: {base: '30px', md: '47px'},
  height: {base: '30px', md: '47px'},
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '1000px',
  backdropFilter: 'blur(15px)',
  transition: 'opacity 0.2s ease',
  sx: {
    background: TEASER_TILE_PLUS_BG,
    '& > svg': {
      display: 'block',
      transformOrigin: 'center',
      transition: 'transform 0.35s ease',
    },
  },
  _hover: {
    cursor: 'pointer',
    opacity: 0.92,
    '& > svg': {
      transform: 'rotate(90deg)',
    },
  },
};

/**
 * 24×24 plus (Figma 3132-3489); unique clipPath id for multiple tiles.
 */
function TeaserTilePlusIcon() {
  const uid = useId();
  const clipId = `teaser-tile-plus-${uid.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <Box
      as="svg"
      aria-hidden
      flexShrink={0}
      height="24px"
      viewBox="0 0 24 24"
      width="24px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${clipId})`}>
        <path d="M0.75 12H23.25" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        <path d="M12 0.75V23.25" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect fill="white" height="24" rx="12" width="24" />
        </clipPath>
      </defs>
    </Box>
  );
}

const CLOSE_BUTTON_STYLES = {
  position: 'absolute',
  top: 4,
  right: 4,
  width: '32px',
  height: '32px',
  minWidth: '32px',
  zIndex: 2,
  color: 'white',
  bg: 'transparent',
  _hover: {bg: 'transparent', opacity: 0.85},
};

const TeaserTileDescriptions = memo(function TeaserTileDescriptions({items, isComplete, isFetching}) {
  return (
    <Suspense fallback={null}>
      <Stack spacing={4}>
        {items.map((item, index) => (
          <Box key={item.sys.id} padding={index === 0 ? 0 : '0 40px'}>
            <ContentfulComponent component={item} isComplete={isComplete} isFetching={isFetching} />
          </Box>
        ))}
      </Stack>
    </Suspense>
  );
});

TeaserTileDescriptions.displayName = 'TeaserTileDescriptions';
TeaserTileDescriptions.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      sys: PropTypes.shape({id: PropTypes.string.isRequired}).isRequired,
    }).isRequired
  ).isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};

export const TeaserTile = ({
  component,
  isComplete = true,
  isFetching = false,
  imageWrapperStyles,
  imageDisplayBlockStyles,
  ...props
}) => {
  const {teaserText, image, descriptionCollection, openInNewTab, link} = component;
  const {isOpen, onOpen, onClose} = useDisclosure();
  const descriptionItems = (descriptionCollection?.items ?? []).filter(Boolean);
  const hasDescription = descriptionItems.length > 0;

  // KL-1043 — teaser tile link (makes the teaser text clickable). Resolve internal
  // vs external the same way the Image model does so internal paths route client-side.
  const isExternalLink =
    link && (link.startsWith('http://') || link.startsWith('https://') || link.startsWith('//'));
  const parsedLink = !isExternalLink && link ? urlHelper.parseStorefrontUrl(link) : null;
  const teaserLinkUrl = parsedLink?.appUrl || link;

  const scrollLockRef = useRef({y: 0, restoreMobile: false});

  const handleOpenDrawer = useCallback(() => {
    if (typeof window !== 'undefined') {
      scrollLockRef.current = {
        y: window.scrollY,
        restoreMobile: window.matchMedia(MOBILE_MAX_WIDTH_MEDIA).matches,
      };
      void loadKingLivingContentfulComponent();
    }

    onOpen();
  }, [onOpen]);

  const handleDrawerCloseComplete = useCallback(() => {
    const {y, restoreMobile} = scrollLockRef.current;

    if (!restoreMobile) {
      return;
    }
    restoreScrollAfterBodyUnlock(y);
  }, []);

  return (
    <Box {...props} {...imageWrapperStyles}>
      <Box
        data-slot="image-display-block"
        overflow="hidden"
        position="relative"
        className="teaser-tile-image-display-block"
        {...imageDisplayBlockStyles}
      >
        {image && (
          <Image className="teaser-tile-image" height="100%" component={image} isComplete={isComplete} isFetching={isFetching} />
        )}
        {hasDescription && (
          <Box as="button" type="button" {...PLUS_ICON_STYLES} onClick={handleOpenDrawer}>
            <TeaserTilePlusIcon />
          </Box>
        )}
      </Box>

      {teaserText && (() => {
        const teaserTextNode = (
          <Text
            color="brandColors.greyscale90"
            fontSize={{base: '14px', lg: '20px'}}
            fontWeight={500}
            lineHeight="120%"
            mt={1}
          >
            {teaserText}
          </Text>
        );

        // KL-1043 — when a link is configured, the teaser text itself is the
        // clickable target (the old standalone "View more" link was removed).
        return link ? (
          <BaseLink
            className="teaser-tile-link"
            href={isExternalLink ? teaserLinkUrl : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            target={openInNewTab ? '_blank' : '_self'}
            to={!isExternalLink ? teaserLinkUrl : undefined}
            _hover={{textDecoration: 'underline'}}
          >
            {teaserTextNode}
          </BaseLink>
        ) : (
          teaserTextNode
        );
      })()}

      {hasDescription && (
        <Drawer
          isOpen={isOpen}
          placement="right"
          size="md"
          blockScrollOnMount
          onClose={onClose}
          onCloseComplete={handleDrawerCloseComplete}
        >
          <DrawerOverlay backdropFilter="blur(8px)" bg="blackAlpha.300" />
          <DrawerContent bg="brandColors.primaryBG" position="relative">
            <IconButton
              aria-label="Close details panel"
              icon={<CloseIcon boxSize="32px" />}
              variant="ghost"
              onClick={onClose}
              {...CLOSE_BUTTON_STYLES}
            />
            <DrawerBody padding="0">
              <TeaserTileDescriptions isComplete={isComplete} isFetching={isFetching} items={descriptionItems} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
};

TeaserTile.displayName = 'TeaserTile';
TeaserTile.propTypes = {
  component: PropTypes.object.isRequired,
  imageDisplayBlockStyles: PropTypes.object,
  imageWrapperStyles: PropTypes.object,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
